import { useCallback } from "react";
import { useAtomValue } from "jotai";
import {
  comparePitsFormSubmittedAtom,
  useFormStateAtoms,
} from "../ComparativeAnalysisForm.atoms";
import { DEFAULT_TASK } from "../ComparativeAnalysisForm.constants";
import type { SearchParamsType } from "../ComparativeAnalysisForm.types";
import { useSetLocalFormState } from "./useLocalFormGroupsState";
import { useUpdateSearchParams } from "./useUpdateSearchParams";

type SwappedGroupsState = SearchParamsType & {
  pipelineId1?: string;
  pipelineId2?: string;
};

/**
 * @returns a function that swaps the groups' values
 */
export const useSwapGroups = () => {
  const isFormSubmitted = useAtomValue(comparePitsFormSubmittedAtom);
  const updateSearchParams = useUpdateSearchParams();

  const formState = useFormStateAtoms();
  const setLocalFormState = useSetLocalFormState();

  return useCallback(() => {
    // if the task is the default task, don't include it in the new group
    const g1 = {
      env1: formState.env2[0],
      pipeline1: formState.pipeline2[0],
      pipelineId1: formState.pipelineId2[0],
      pit1: formState.pit2[0],
      ...(formState.task2[0] !== DEFAULT_TASK && { task1: formState.task2[0] }),
    };

    const g2 = {
      env2: formState.env1[0],
      pipeline2: formState.pipeline1[0],
      pipelineId2: formState.pipelineId1[0],
      pit2: formState.pit1[0],
      ...(formState.task1[0] !== DEFAULT_TASK && { task2: formState.task1[0] }),
    };

    const merged: SwappedGroupsState = { ...g1, ...g2 };

    setLocalFormState(merged);

    if (isFormSubmitted) {
      const { pipelineId1: _p1, pipelineId2: _p2, ...forUrl } = merged;
      updateSearchParams(forUrl);
    }
  }, [formState, setLocalFormState, isFormSubmitted, updateSearchParams]);
};
